import { useEffect, useState, useCallback } from 'react';
import { listSites, type Site } from './db';
import { useUserOrg } from './org';
import { cacheListByOrg } from './cache';

const KEY = 'qhs:currentSiteId';

// Tiny pub/sub so multiple components stay in sync when the active site changes.
const listeners = new Set<() => void>();
function notify() { listeners.forEach((fn) => fn()); }

/**
 * Reads/writes the currently active site for this device. Auto-picks the first
 * site if none is stored, and falls back gracefully if the stored site has been
 * archived or deleted.
 */
export function useCurrentSite() {
  const { org } = useUserOrg();
  const [storedId, setStoredId] = useState<string | null>(
    () => localStorage.getItem(KEY)
  );
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to changes from other components
  useEffect(() => {
    const fn = () => setStoredId(localStorage.getItem(KEY));
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);

  // Load sites + reconcile stored id whenever the org changes.
  //
  // Stale-while-revalidate: first paint reads sites from the IndexedDB
  // cache (sub-50ms on a warm cache) so the page can render immediately
  // with last-known data. The fresh fetch then runs silently in the
  // background and swaps in any updates when it returns. On a cold
  // cache (first ever load) we fall through to the old behaviour —
  // loading=true until the network returns.
  useEffect(() => {
    if (!org) { setLoading(false); return; }
    let cancelled = false;
    let haveCache = false;

    // Phase 1: instant render from cache. cacheListByOrg returns
    // synchronously-fast from IndexedDB; if any sites are cached we
    // render with them and clear loading so the user sees content
    // immediately.
    cacheListByOrg<Site>('sites', org.org_id).then((cached) => {
      if (cancelled) return;
      if (cached.length > 0) {
        haveCache = true;
        setSites(cached);
        setLoading(false);
      } else {
        // No cached data — keep loading=true so caller can render
        // its loading indicator.
        setLoading(true);
      }
    }).catch(() => { /* cache miss is fine, network is authoritative */ });

    // Phase 2: fresh network fetch in the background. Swaps in new
    // data when it lands, regardless of what was cached.
    listSites(org.org_id)
      .then((list) => {
        if (cancelled) return;
        setSites(list);
        const stored = localStorage.getItem(KEY);
        if (list.length === 0) {
          if (stored) { localStorage.removeItem(KEY); notify(); }
          return;
        }
        if (!stored || !list.find((s) => s.id === stored)) {
          localStorage.setItem(KEY, list[0].id);
          notify();
        }
      })
      .catch(() => { /* listSites already falls back to cache on error */ })
      .finally(() => { if (!cancelled && !haveCache) setLoading(false); });
    return () => { cancelled = true; };
  }, [org]);

  const currentSite = storedId ? sites.find((s) => s.id === storedId) ?? null : null;

  const setCurrentSite = useCallback((siteId: string) => {
    localStorage.setItem(KEY, siteId);
    notify();
  }, []);

  return { currentSite, currentSiteId: currentSite?.id ?? null, sites, setCurrentSite, loading };
}
